import { EntityId, createAsyncThunk } from '@reduxjs/toolkit';
import {
  FavoritesReducerStateShape,
  clientFavoritesSlice,
  currentClientFavoriteApi,
  isClientFavoriteSelector,
} from '@cp/entities/client/current/favorite';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useCallback } from 'react';
import { FavoritesService } from './favoritesService';
import { useFavoritesService } from './useFavoritesService';

type ToggleFavoritesThunkParams = {
  clientId: EntityId;
  isLoggedIn: boolean;
  favoritesService: FavoritesService;
};

const toggleFavoriteThunk = createAsyncThunk<void, ToggleFavoritesThunkParams, { state: FavoritesReducerStateShape }>(
  'user/toggleFavorite',
  async ({ clientId, favoritesService, isLoggedIn }, { dispatch, getState }) => {
    if (!isLoggedIn) {
      dispatch(clientFavoritesSlice.actions.toggleFavorite(clientId));
      favoritesService.setFavorites(getState().clientFavorites);
      return;
    }

    const isFavorite = isClientFavoriteSelector(getState(), clientId);
    const initiate = isFavorite
      ? currentClientFavoriteApi.endpoints.deleteFavoriteClient.initiate
      : currentClientFavoriteApi.endpoints.createFavoriteClient.initiate;

    // optimistic update
    dispatch(clientFavoritesSlice.actions.toggleFavorite(clientId));

    try {
      await dispatch(initiate({ clientId: clientId as number })).unwrap();
    } catch (err) {
      // rollback optimistic update
      dispatch(clientFavoritesSlice.actions.toggleFavorite(clientId));
      throw err;
    }
  },
);

export const useToggleFavoriteThunk = () => {
  const isLoggedIn = useIsLoggedIn();
  const favoritesService = useFavoritesService();
  return useCallback(
    (clientId: EntityId) => toggleFavoriteThunk({ clientId, favoritesService, isLoggedIn }),
    [favoritesService, isLoggedIn],
  );
};
